import {
    NotAuthorizedException,
    PasswordResetRequiredException,
    UserNotFoundException,
} from "@aws-sdk/client-cognito-identity-provider"
import { CookieSerializeOptions } from "cookie"
import type { IncomingHttpHeaders } from "http"
import { z } from "zod"

export enum CredentialName {
    accessToken = "accessToken",
    expires = "expires",
    idTokenCookie = "idTokenCookie",
    refreshExpires = "refreshExpires",
    refreshTokenCookie = "refreshTokenCookie",
    deviceKey = "deviceKey",
    lastUsed = "lastUsed",
    accessSignature = "accessSignature",
    registrationCookie = "registrationCookie",
    passwordResetCookie = "passwordResetCookie",
    challengeSessionToken = "challengeSessionToken",
    issuedAt = "issuedAt",
}

export type CognitoUserPayload = { id: string; email: string; firstName: string; lastName: string }

export const NovaAuthExceptions = Object.freeze({
    userNotFound: UserNotFoundException,
    passwordResetRequired: PasswordResetRequiredException,
    notAuthorizedException: NotAuthorizedException,
})

export enum NovaAuthStrategy {
    COOKIES = "cookies",
    HEADERS = "nova-headers",
}

export enum NovaAuthMode {
    NOVA_COOKIES = "novaCookies",
    NOVA_TOKENS = "novaTokens",
    NOVA_API = "novaAPI",
    DJI_TOKEN = "djiToken",
    AGORA_API = "agoraAPI",
    SKYDIO_API = "skydioAPI",
    UNKNOWN = "unknown",
}
export type AuthenticateUserParameters = {
    email: string
    password: string
    customClientId?: string
}

export const AuthenticatedCognitoUserSchema = z.object({
    id: z.string(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    groups: z.array(z.string()).optional(),
})

export type AuthenticatedCognitoUser = z.infer<typeof AuthenticatedCognitoUserSchema>

export type NovaAuthenticatedUser = AuthenticatedCognitoUser

export type NovaAuthCookies = Record<
    string,
    ({ value: string } & CookieSerializeOptions) | undefined
>
export type NovaAuthTokens = Record<string, { value: string }>

export type NovaAuthRequestHeaders = IncomingHttpHeaders

export interface AppleIdToken {
    iss: string
    aud: string
    exp: number
    iat: number
    sub: string
    email?: string
    email_verified: boolean
    nonce_supported: boolean
    c_hash?: string
    auth_time?: number
    org_id?: string
}

export const AppleAuthRequestSchema = z.object({
    state: z.string().nullable(),
    authorizationCode: z.string(),
    fullName: z
        .object({
            nickname: z.string().nullable(),
            namePrefix: z.string().nullable(),
            middleName: z.string().nullable(),
            givenName: z.string().nullable(),
            nameSuffix: z.string().nullable(),
            familyName: z.string().nullable(),
        })
        .optional(),
    email: z.email().nullable(),
    identityToken: z.string(),
})
export type AppleAuthRequest = z.infer<typeof AppleAuthRequestSchema>

export type GoogleIdToken = {
    iss: string
    aud: string
    exp: number
    iat: number
    sub: string
    email?: string
    email_verified?: boolean
    name?: string
    given_name?: string
    family_name?: string
    picture?: string
    locale?: string
    hd?: string
}

export const GoogleAuthRequestSchema = z.object({
    code: z.string(),
    clientId: z.string(),
    codeVerifier: z.string(),
    redirectUri: z.string(),
})
export type GoogleAuthRequest = z.infer<typeof GoogleAuthRequestSchema>

export type MicrosoftIdToken = {
    iss: string
    aud: string
    exp: number
    iat: number
    sub: string
    preferred_username?: string
    name?: string
    oid?: string
    tid?: string
    ver?: string
    nbf?: number
}

export const MicrosoftAuthRequestSchema = z.object({
    code: z.string(),
    clientId: z.string(),
    /**
     * This property will only exist for requests initiated by mobile applications
     * which follow the PKCE flow. Web applications do not use PKCE for Microsoft.
     */
    codeVerifier: z.string().optional(),
    redirectUri: z.string(),
    sessionState: z.string().optional(),
    state: z.string().optional(),
})
export type MicrosoftAuthRequest = z.infer<typeof MicrosoftAuthRequestSchema>

export type FederatedUserInfo = {
    email: string
    email_verified: boolean
    sub: string
    aud: string
    firstName?: string
    lastName?: string
}
